import { UTILS } from '@aksia/infrastructure';

export enum ExtractionDataPointTypeEnum {
  Intramonth = 1,
  Mtd,
  Aum,
  Term,
  Exposure,
  CashFlow,
  DocumentClassification,
}

export namespace ExtractionDataPointTypeEnum {
  const replace = [{ key: /([a-z])([A-Z])/g, value: '$1 $2' }];
  export const toOptions = () =>
    UTILS.UI.enumToOptions(ExtractionDataPointTypeEnum, replace);
}

export enum ExtractionDataPointTypeActionEnum {
  AssignEntity = 1,
  UpdateReturnAmount,
  UpdateReturnSource,
  UpdateReturnFee,
  UpdateReturnClassification,
  UpdateReturnCurrency,
  UpdateReturnDate,
  UpdateAumAmount,
  UpdateAumCurrency,
  UpdateAumDate,
  UpdateAumSource,
  UpdateDocumentEffectiveDate,
  UpdateDocumentType,
  UpdateDocumentFunds,
  UpdateDocumentClients,
}

export enum DocumentTypeEnum {
  CashFlowTransaction = 232,
  CapitalAccountStatement = 233,
  Returns = 234,
  FinancialStatements = 235,
  Reports = 236,
  GeneralCorrespondence = 237,
  LegalDocument = 238,
  CashStatement = 246,
  FeeTransparency = 247,
  TaxDocuments = 250,
  RESurvey = 271,
  SubscriptionDocuments = 563,
  SideLetters = 564,
  AmendmentProxy = 565,
  LimitedPartnershipOrManagementAgreement = 566,
  MeetingsCalls = 567,
  UnderlyingAssets = 568,
  AuditedFinancialStatements = 1213,
  Pitchbook = 1327,
  FinalInvestmentMemo = 1328,
  FinalTradeBlotter = 1329,
  IlpaStatement = 1473,
  Other = 1474,
}

export namespace DocumentTypeEnum {
  const LABELS: Record<DocumentTypeEnum, string> = {
    [DocumentTypeEnum.CashFlowTransaction]: 'Cash Flow/Transaction',
    [DocumentTypeEnum.CapitalAccountStatement]: 'Capital Account Statement',
    [DocumentTypeEnum.Returns]: 'Returns',
    [DocumentTypeEnum.FinancialStatements]: 'Financial Statements',
    [DocumentTypeEnum.Reports]: 'Reports',
    [DocumentTypeEnum.GeneralCorrespondence]: 'General Correspondence',
    [DocumentTypeEnum.LegalDocument]: 'Legal Document',
    [DocumentTypeEnum.CashStatement]: 'Cash Statement',
    [DocumentTypeEnum.FeeTransparency]: 'Fee Transparency',
    [DocumentTypeEnum.TaxDocuments]: 'Tax Documents',
    [DocumentTypeEnum.RESurvey]: 'RE Survey',
    [DocumentTypeEnum.SubscriptionDocuments]: 'Subscription Documents',
    [DocumentTypeEnum.SideLetters]: 'Side Letters',
    [DocumentTypeEnum.AmendmentProxy]: 'Amendment/Proxy',
    [DocumentTypeEnum.LimitedPartnershipOrManagementAgreement]:
      'Limited Partnership or Management Agreement',
    [DocumentTypeEnum.MeetingsCalls]: 'Meetings / Calls',
    [DocumentTypeEnum.UnderlyingAssets]: 'Underlying Assets',
    [DocumentTypeEnum.AuditedFinancialStatements]:
      'Audited Financial Statements',
    [DocumentTypeEnum.Pitchbook]: 'Pitchbook',
    [DocumentTypeEnum.FinalInvestmentMemo]: 'Final Investment Memo',
    [DocumentTypeEnum.FinalTradeBlotter]: 'Final Trade Blotter',
    [DocumentTypeEnum.IlpaStatement]: 'ILPA Statement',
    [DocumentTypeEnum.Other]: 'Other',
  };

  export const toOptions = (): Array<{
    label: string;
    value: DocumentTypeEnum;
  }> =>
    Object.entries(LABELS).map(([value, label]) => ({
      label,
      value: Number(value) as DocumentTypeEnum,
    }));
}

export enum ExtractionSourceTypeEnum {
  Email = 1,
  EmailAttachment,
  ClientOpsAttachment,
  ClientOps,
  DataTeamManualUpload,
  MonitoringManualUpload,
}

export namespace ExtractionSourceTypeEnum {
  const replace = [{ key: /([a-z])([A-Z])/g, value: '$1 $2' }];
  export const toOptions = () =>
    UTILS.UI.enumToOptions(ExtractionSourceTypeEnum, replace);
}

export enum ExtractionStatusEnum {
  Received = 1,
  Processing = 2,
  Ready = 3,
  Checked = 4,
  Skipped = 5,
  Error = 6,
  InProgress = 7,
}

export namespace ExtractionStatusEnum {
  const replace = [{ key: /([a-z])([A-Z])/g, value: '$1 $2' }];
  export const toOptions = () =>
    UTILS.UI.enumToOptions(ExtractionStatusEnum, replace);
}

export enum ExtractionDataPointStatusEnum {
  Pending = 1,
  Transferred = 2,
  Skipped = 3,
}

export namespace ExtractionDataPointStatusEnum {
  const replace = [{ key: /([a-z])([A-Z])/g, value: '$1 $2' }];
  export const toOptions = () =>
    UTILS.UI.enumToOptions(ExtractionDataPointStatusEnum, replace);
}

export enum ExtractionDataPointEntityType {
  Client = 0,
  Company = 1,
  Program,
  Fund,
}

export enum ExtractionDataPointUpdateActionEnum {
  Transfer,
  Skip,
  Revoke,
}

export enum ExtractionUpdateActionEnum {
  Skip,
  Check,
}

export enum ExtractionDataPointRecommendationEnum {
  None,
  NotFound,
  Match,
  Mismatch,
  Backfill,
}

export namespace ExtractionDataPointRecommendationEnum {
  const replace = [{ key: /([a-z])([A-Z])/g, value: '$1 $2' }];
  export const toOptions = () =>
    UTILS.UI.enumToOptions(ExtractionDataPointRecommendationEnum, replace);
}

export enum ExtractionFilterKeywordScopeEnum {
  All = 0,
  Subject = 1,
  Body = 2,
  From = 3,
}

export enum ExtractionSortByEnum {
  TimeSended, //Back-end value: ReceivedAt
  Status,
}

export enum ExtractionSortDirectionEnum {
  Desc,
  Asc,
}

export enum EmailExtractionTagEnum {
  ThirdParty = '3rd Party',
  PC = 'PC',
  ED = 'ED',
  Asia = 'Asia',
  LS = 'LS',
  IndexAndResearch = 'Index & Research',
  Priority = 'Priority',
  CapIntro = 'Cap Intro',
  RV = 'RV',
  FOF = 'FOF',
}
export namespace EmailExtractionTagEnum {
  export const toOptions = () => UTILS.UI.enumToOptions(EmailExtractionTagEnum);
  export const toValueArray = () =>
    UTILS.UI.enumToValueArray(EmailExtractionTagEnum) as string[];
}
